import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../api/AccountsAPI/verifyCloseMasterAPI';

const err = (e, fallback) => e?.ErrorDetails?.ErrorMessage || e?.Message || e?.message || fallback;

export const fetchCloseQueue = createAsyncThunk(
    'verifyCloseMaster/fetchQueue',
    async ({ route, params }, { rejectWithValue }) => {
        try { return await api.getCloseQueue(route, params); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch the closing verification queue')); }
    }
);

export const fetchCloseDetail = createAsyncThunk(
    'verifyCloseMaster/fetchDetail',
    async ({ route, params, key }, { rejectWithValue }) => {
        try {
            const data = await api.getClosePendings(route, params);
            return { key, data };
        } catch (e) { return rejectWithValue(err(e, 'Failed to fetch closing details')); }
    }
);

export const submitCloseNotification = createAsyncThunk(
    'verifyCloseMaster/submit',
    async ({ route, payload }, { rejectWithValue }) => {
        try { return await api.saveCloseNotification(route, payload); }
        catch (e) { return rejectWithValue(err(e, 'Error Occurred While Sending Notification.')); }
    }
);

const initialState = {
    queue: [], queueLoading: false, queueError: null,
    // detailKey = the queue row the detail belongs to (types key rows differently)
    detail: null, detailKey: null, detailLoading: false, detailError: null,
    submitLoading: false,
};

const verifyCloseMasterSlice = createSlice({
    name: 'verifyCloseMaster',
    initialState,
    reducers: {
        clearCloseDetail: (s) => { s.detail = null; s.detailKey = null; s.detailError = null; s.detailLoading = false; },
        resetVerifyCloseMaster: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCloseQueue.pending, (s) => { s.queueLoading = true; s.queueError = null; })
            .addCase(fetchCloseQueue.fulfilled, (s, a) => { s.queueLoading = false; s.queue = a.payload?.Data || []; })
            .addCase(fetchCloseQueue.rejected, (s, a) => { s.queueLoading = false; s.queueError = a.payload; s.queue = []; });

        builder
            .addCase(fetchCloseDetail.pending, (s) => { s.detailLoading = true; s.detailError = null; s.detail = null; s.detailKey = null; })
            .addCase(fetchCloseDetail.fulfilled, (s, a) => {
                s.detailLoading = false;
                s.detail = a.payload?.data?.Data || null;
                s.detailKey = a.payload?.key ?? null;
            })
            .addCase(fetchCloseDetail.rejected, (s, a) => { s.detailLoading = false; s.detailError = a.payload; s.detail = null; });

        builder
            .addCase(submitCloseNotification.pending, (s) => { s.submitLoading = true; })
            .addCase(submitCloseNotification.fulfilled, (s) => { s.submitLoading = false; })
            .addCase(submitCloseNotification.rejected, (s) => { s.submitLoading = false; });
    },
});

export const { clearCloseDetail, resetVerifyCloseMaster } = verifyCloseMasterSlice.actions;

export const selectVCMQueue = (s) => s.verifyCloseMaster.queue;
export const selectVCMQueueLoading = (s) => s.verifyCloseMaster.queueLoading;
export const selectVCMQueueError = (s) => s.verifyCloseMaster.queueError;
export const selectVCMDetail = (s) => s.verifyCloseMaster.detail;
export const selectVCMDetailKey = (s) => s.verifyCloseMaster.detailKey;
export const selectVCMDetailLoading = (s) => s.verifyCloseMaster.detailLoading;
export const selectVCMDetailError = (s) => s.verifyCloseMaster.detailError;
export const selectVCMSubmitLoading = (s) => s.verifyCloseMaster.submitLoading;

export default verifyCloseMasterSlice.reducer;
